// ─── "No la encuentro" no es "no la traje" (issue #507) ──────────────────────
//
// La pantalla de accesos resuelve el vínculo de cada evento contra un mapa de
// inspecciones que arma con UN `GET /yardInspectionList` de `limit: 500`,
// mientras los eventos vienen de otro con `limit: 1000`. Son dos techos fijos
// distintos comparados entre sí, y nada garantiza que las 500 inspecciones
// cubran a los 1000 eventos: un evento cuya inspección quedó fuera de esa
// página se marcaba `inspection_unknown` —"Inspección vinculada no
// encontrada"— sin que faltara absolutamente nada.
//
// Eso importa porque la alerta es acusatoria: en la tarjeta se lee como "esta
// unidad entró sin inspección", que es un incidente operativo real, y el
// operador no tenía forma de distinguirla de un artefacto de paginado.
//
// El arreglo de fondo está fuera de esta pantalla y fuera del cliente: o el
// módulo expone un `GET /yardInspection?id=` (hoy sólo hay el listado, el PUT y
// las rutas de evidencia) o `/yardAccessEventList` devuelve junto a cada evento
// el estado de su inspección. Cualquiera de las dos deja además borrar el
// barrido de 500 por completo. Lo que sí se puede hacer del lado del cliente, y
// es lo que hace este módulo, es dejar de AFIRMAR lo que no se sabe.
//
// Por qué no se arregla trayendo más páginas acá: `loadInspections` corre en el
// poll de 5 s, y `/yardInspectionList` lee TODOS los documentos de la empresa
// en memoria por request (Firestore no sabe expresar el OR de createdAt /
// entry_timestamp / exit_timestamp) con una subconsulta de `evidences` por
// documento. Paginar hasta cubrir el total convertiría un barrido cada 5 s en
// `ceil(total/500)` barridos cada 5 s — a 5 000 inspecciones son 120 requests
// por minuto y por pestaña abierta, cada uno un escaneo completo. El historial
// sí pagina (fetchAllYardInspections) porque ahí el rango es el día en curso;
// acá el conjunto NO puede acotarse por fecha —una salida de hoy referencia la
// inspección de una visita que entró hace días: hay folios abiertos 120 h,
// 106 h y 34 h en Acuapue, y 11 de las 20 tarjetas nunca fueron leídas por la
// lectora de salida—, así que es la colección entera o nada.
//
// Vive fuera de page.tsx porque ahí no se puede testear: ese módulo importa
// `utils/api` → firebase, y un worktree no recibe `apps/v5/.env`, así que el
// archivo ni siquiera colecta.

export type InspectionLinkState =
  /** El evento no declara inspección. Es un hecho del documento. */
  | "absent"
  /** Todavía no llegó el conjunto: no se afirma nada. */
  | "pending"
  /** Resuelta contra el mapa. */
  | "linked"
  /** El conjunto está completo y no está: la inspección de verdad falta. */
  | "not_found"
  /** El conjunto vino truncado: no se sabe, y decir "falta" sería mentir. */
  | "unverified";

export interface InspectionLinkInput {
  inspectionId: string | null | undefined;
  /** `inspectionsLoaded`: llegó una respuesta real para el filtro vigente. */
  loaded: boolean;
  known: { has(id: string): boolean } | null | undefined;
  /**
   * El conjunto cargado es TODO lo que el filtro abarca. Sale de comparar lo
   * que llegó contra el `total` que informa la paginación del backend, no de
   * suponer que 500 alcanzan.
   */
  complete: boolean;
}

export function resolveInspectionLink(
  input: InspectionLinkInput,
): InspectionLinkState {
  const { inspectionId, loaded, known, complete } = input;

  // Primero, porque no depende de nada cargado: el evento no apunta a ninguna
  // inspección y eso es cierto venga truncado el conjunto o no.
  if (!inspectionId) return "absent";

  // `loaded` vuelve a false en cada cambio de patio, a propósito: sin eso los
  // eventos del patio nuevo se evaluaban contra el mapa del patio ANTERIOR y la
  // pantalla parpadeaba una alerta amarilla en TODAS las tarjetas.
  if (!loaded || !known) return "pending";

  if (known.has(inspectionId)) return "linked";
  return complete ? "not_found" : "unverified";
}
